// Dialog for connecting a builder app to a git repo.
//
// Provider-aware (today only github; gitlab/bitbucket would slot in via
// PROVIDER_META). UX:
//   1. If the user has no Composio integration for the selected provider,
//      embed the standard Composio OAuth connect flow (popup + status poll),
//      then finalize via /integrations/composio/install-generic.
//   2. Once at least one integration exists, the user picks one and enters
//      either an existing owner/repo or a new repo name. On submit we POST
//      /apps/<id>/git/link.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "../../../components/LoadingButton";
import LucideIcon from "../../../components/LucideIcon";
import {
  startComposioConnect,
} from "../../integrations/api";
import { GIT_PROVIDER_GITHUB, INTEGRATION_TYPE_GITHUB } from "../../../constants";
import type { BuilderApiClient } from "../api";
import type {
  GitIntegrationSummary,
  GitProvider,
  GitRepoSummary,
  GitSyncStatus,
} from "../types";

// Per-provider Composio plumbing + display strings. To add a new provider,
// extend the GitProvider union in types.ts and add an entry here.
interface ProviderMeta {
  label: string;
  composioIntegrationType: string;
  composioToolkit: string;
  defaultBranch: string;
}

const PROVIDER_META: Record<GitProvider, ProviderMeta> = {
  [GIT_PROVIDER_GITHUB]: {
    label: "GitHub",
    composioIntegrationType: INTEGRATION_TYPE_GITHUB,
    composioToolkit: "GITHUB",
    defaultBranch: "main",
  },
};

interface Props {
  open: boolean;
  onClose: () => void;
  api: BuilderApiClient;
  appId: string;
  companyId: string;
  getToken: () => Promise<string>;
  onLinked: (status: GitSyncStatus) => void;
  provider?: GitProvider;
}

type Mode = "existing" | "new";

const SAFE_URL = /^https?:\/\/[a-zA-Z0-9][a-zA-Z0-9.\-:/\\?&=%_+~#]+$/;

const GitLinkDialog: React.FC<Props> = ({
  open,
  onClose,
  api,
  appId,
  companyId,
  getToken,
  onLinked,
  provider = GIT_PROVIDER_GITHUB,
}) => {
  const meta = PROVIDER_META[provider];

  const [integrations, setIntegrations] = useState<GitIntegrationSummary[]>([]);
  const [loadingIntegrations, setLoadingIntegrations] = useState(false);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState("");

  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState("");

  const [mode, setMode] = useState<Mode>("new");
  const [newRepoName, setNewRepoName] = useState("");
  const [privateRepo, setPrivateRepo] = useState(true);
  const [repos, setRepos] = useState<GitRepoSummary[]>([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [reposError, setReposError] = useState("");
  const [selectedRepo, setSelectedRepo] = useState<GitRepoSummary | null>(null);
  const [branch, setBranch] = useState(meta.defaultBranch);

  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState("");

  // Stable refs so reload() doesn't get a new identity on every parent render.
  // Without this, the open-effect re-fires whenever the parent re-renders and
  // racy reloads flash the empty state.
  const apiRef = useRef(api);
  apiRef.current = api;
  const providerRef = useRef(provider);
  providerRef.current = provider;

  const reload = useCallback(async () => {
    setLoadingIntegrations(true);
    try {
      const list = await apiRef.current.listGitIntegrations(appId, providerRef.current);
      setIntegrations(list);
      setSelectedIntegrationId((prev) => {
        if (prev && list.some((i) => i.id === prev)) return prev;
        return list[0]?.id || "";
      });
    } catch (e: any) {
      setConnectError(e?.response?.data?.error || e?.message || "Failed to load integrations");
    } finally {
      setLoadingIntegrations(false);
    }
  }, [appId]);

  // Reset + reload only when the dialog OPENS (open transitions false → true).
  // Not when reload's identity changes — it's now stable on appId.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setConnectError("");
      setLinkError("");
      reload();
    }
    wasOpen.current = open;
  }, [open, reload]);

  const handleConnect = useCallback(async () => {
    setConnecting(true);
    setConnectError("");
    try {
      const result = await startComposioConnect(
        companyId,
        meta.composioIntegrationType,
        meta.label,
        getToken,
      );
      const redirect = result?.redirectUrl || result?.redirect_url;
      if (!redirect || !SAFE_URL.test(redirect)) {
        throw new Error("Invalid redirect URL from Composio");
      }

      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(
        redirect,
        `${provider}OAuth`,
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`,
      );

      if (!popup || popup.closed) {
        setConnecting(false);
        setConnectError("Popup was blocked. Please allow popups and try again.");
        return;
      }

      // Once the popup closes, the backend list endpoint materializes a local
      // Integration row from whichever Composio account just completed OAuth —
      // no extra install call needed from here.
      const poll = setInterval(async () => {
        if (!popup.closed) return;
        clearInterval(poll);
        try {
          await reload();
        } catch (err: any) {
          setConnectError(err?.response?.data?.error || err?.message || "Failed to refresh integrations");
        } finally {
          setConnecting(false);
        }
      }, 1000);
    } catch (err: any) {
      setConnecting(false);
      setConnectError(err?.response?.data?.error || err?.message || "Failed to start connection");
    }
  }, [companyId, getToken, meta.composioIntegrationType, meta.label, provider, reload]);

  // Fetch the user's repos when an existing-repo flow is active and we have
  // an integration selected. Re-fetch when either changes; ignore stale
  // responses if the user switches integration mid-flight.
  useEffect(() => {
    if (mode !== "existing" || !selectedIntegrationId) return;
    let cancelled = false;
    setLoadingRepos(true);
    setReposError("");
    apiRef.current
      .listGitRepos(appId, selectedIntegrationId, providerRef.current)
      .then((list) => {
        if (cancelled) return;
        setRepos(list);
      })
      .catch((e) => {
        if (cancelled) return;
        setReposError(e?.response?.data?.error || e?.message || "Failed to load repositories");
      })
      .finally(() => {
        if (!cancelled) setLoadingRepos(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, selectedIntegrationId, appId]);

  // Default the branch field to the selected repo's default branch.
  useEffect(() => {
    if (mode === "existing" && selectedRepo) {
      setBranch(selectedRepo.defaultBranch || meta.defaultBranch);
    }
  }, [mode, selectedRepo, meta.defaultBranch]);

  const canSubmit = useMemo(() => {
    if (!selectedIntegrationId) return false;
    if (mode === "new") return !!newRepoName.trim();
    return !!selectedRepo;
  }, [selectedIntegrationId, mode, newRepoName, selectedRepo]);

  const handleSubmit = useCallback(async () => {
    setLinking(true);
    setLinkError("");
    try {
      const status = await api.linkGitRepo(appId, {
        provider,
        integrationId: selectedIntegrationId,
        owner: mode === "new" ? "" : (selectedRepo?.owner || ""),
        repo: mode === "new" ? newRepoName.trim() : (selectedRepo?.repo || ""),
        branch: branch.trim() || meta.defaultBranch,
        createNew: mode === "new",
        private: privateRepo,
      });
      onLinked(status);
      onClose();
    } catch (err: any) {
      setLinkError(err?.response?.data?.error || err?.message || "Failed to link repo");
    } finally {
      setLinking(false);
    }
  }, [api, appId, provider, selectedIntegrationId, mode, selectedRepo, newRepoName, branch, privateRepo, meta.defaultBranch, onLinked, onClose]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Connect to {meta.label}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", right: 8, top: 8 }}
          aria-label="close"
        >
          <LucideIcon name="X" size={18} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {connectError && <Alert severity="error">{connectError}</Alert>}

          {loadingIntegrations && integrations.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Loading connected accounts…
            </Typography>
          ) : integrations.length === 0 ? (
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                You don't have a {meta.label} account connected yet. Connect one to keep your
                app in sync with a repo.
              </Typography>
              <Box>
                <LoadingButton
                  variant="contained"
                  onClick={handleConnect}
                  loading={connecting}
                >
                  Connect {meta.label}
                </LoadingButton>
              </Box>
            </Stack>
          ) : (
            <>
              <TextField
                select
                label={`${meta.label} account`}
                value={selectedIntegrationId}
                onChange={(e) => setSelectedIntegrationId(e.target.value)}
                size="small"
                fullWidth
                helperText={
                  <Box
                    component="span"
                    onClick={handleConnect}
                    sx={{ cursor: "pointer", textDecoration: "underline" }}
                  >
                    Connect another {meta.label} account
                  </Box>
                }
              >
                {integrations.map((i) => (
                  <MenuItem key={i.id} value={i.id}>
                    {i.instanceName}
                  </MenuItem>
                ))}
              </TextField>

              <RadioGroup
                value={mode}
                onChange={(e) => setMode(e.target.value as Mode)}
              >
                <FormControlLabel
                  value="new"
                  control={<Radio />}
                  label="Create a new repo"
                />
                <FormControlLabel
                  value="existing"
                  control={<Radio />}
                  label="Use an existing repo"
                />
              </RadioGroup>

              {mode === "new" ? (
                <Stack spacing={1.5}>
                  <TextField
                    label="Repository name"
                    value={newRepoName}
                    onChange={(e) => setNewRepoName(e.target.value)}
                    size="small"
                    fullWidth
                    placeholder="my-app-sync"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={privateRepo}
                        onChange={(e) => setPrivateRepo(e.target.checked)}
                      />
                    }
                    label="Private repository"
                  />
                </Stack>
              ) : (
                <Stack spacing={1}>
                  <Autocomplete
                    options={repos}
                    value={selectedRepo}
                    onChange={(_, v) => setSelectedRepo(v)}
                    loading={loadingRepos}
                    getOptionLabel={(o) => o.fullName}
                    isOptionEqualToValue={(a, b) => a.fullName === b.fullName}
                    renderOption={(props, option) => (
                      <li {...props} key={option.fullName}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%" }}>
                          <Typography sx={{ flex: 1 }}>{option.fullName}</Typography>
                          {option.private && <Chip size="small" label="private" />}
                        </Stack>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Repository"
                        size="small"
                        placeholder={loadingRepos ? "Loading…" : "Search repos"}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingRepos ? <CircularProgress size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  {reposError && <Alert severity="error">{reposError}</Alert>}
                </Stack>
              )}

              <TextField
                label="Branch"
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                size="small"
                fullWidth
              />

              {linkError && <Alert severity="error">{linkError}</Alert>}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <LoadingButton onClick={onClose} disabled={linking}>
          Cancel
        </LoadingButton>
        {integrations.length > 0 && (
          <LoadingButton
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={linking}
          >
            Link repo
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GitLinkDialog;
